import type { UserLocationDetails } from "../../Dashboard/dashboard.component";
import type { LocationInventory } from "../../FloorManager/typings/location-inventory";
import type { LocationUsage } from "../../Reports/typings/reports.types";

/** Backend sentinel: location name when the user booked mobile-working. */
export const MOBILE_WORKING_LOCATION_NAME = "Mobile Working";

/** Backend sentinel: location name placeholder when no booking was made. */
export const NO_BOOKING_TIME_LOCATION_NAME = "No Booking Time";

export const getFavoriteLocationKey = (location: LocationUsage): string =>
  `${location.locationInventoryId}-${location.locationName}`;

/** Physical sites only — mobile working is a work status, not a favorite location. */
export const isDashboard2FavoriteLocation = (usage: LocationUsage): boolean =>
  !usage.isMobileWorking && usage.locationName !== MOBILE_WORKING_LOCATION_NAME;

export const getDashboard2FavoriteLocations = (
  userReport: UserLocationDetails[] | undefined,
  showBookingStatistics: boolean
): LocationUsage[] =>
  userReport?.[0]?.duration?.filter(
    usage =>
      isDashboard2FavoriteLocation(usage) &&
      (usage.locationName !== NO_BOOKING_TIME_LOCATION_NAME || showBookingStatistics)
  ) ?? [];

/** Mobile-working site from user report — used for status-sheet mobile bookings (not a favorite). */
export const getDashboard2MobileWorkingLocation = (
  userReport: UserLocationDetails[] | undefined
): LocationUsage | undefined =>
  userReport?.[0]?.duration?.find(
    usage => usage.isMobileWorking || usage.locationName === MOBILE_WORKING_LOCATION_NAME
  );

const isMobileWorkingInventory = (inventory: LocationInventory): boolean =>
  inventory.isMobileWorking || inventory.name === MOBILE_WORKING_LOCATION_NAME;

/** Fallback when usage report has no mobile-working row (e.g. no mobile bookings last month). */
export const getDashboard2MobileWorkingLocationFromInventories = (
  locations: readonly LocationInventory[] | undefined
): LocationUsage | undefined => {
  const inventory = locations?.find(isMobileWorkingInventory);
  if (!inventory) {
    return undefined;
  }

  return {
    locationInventoryId: inventory.id,
    locationName: inventory.name,
    locationColor: inventory.color,
    absoluteDuration: 0,
    relativeDuration: 0,
    isMobileWorking: true,
    picturePath: inventory.picturePath ?? undefined
  };
};

export const resolveDashboard2MobileWorkingLocation = (
  userReport: UserLocationDetails[] | undefined,
  companyLocations: readonly LocationInventory[] | undefined
): LocationUsage | undefined =>
  getDashboard2MobileWorkingLocation(userReport) ??
  getDashboard2MobileWorkingLocationFromInventories(companyLocations);
